import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Palette,
  Search,
  Filter,
  Clock,
  Eye,
  MoreVertical,
  Grid3X3,
  List,
  Paintbrush,
} from "lucide-react";
import { useConversationStore } from "@/stores/conversationStore";
import { useNavigate } from "@tanstack/react-router";
import { Sidebar } from "@/components/chat/Sidebar";

export const Route = createFileRoute("/styles")({
  component: StylesPage,
});

interface DesignKit {
  id: string;
  title: string;
  thumbnail?: string;
  previewImage?: string;
  status: "active" | "draft" | "archived";
  lastModified: Date;
  createdAt: Date;
  styleCount?: number;
  category: string;
  tags: string[];
  description?: string;
  colorPalette?: string[];
  fontFamily?: string;
}

function StylesPage() {
  const navigate = useNavigate();
  const { conversations } = useConversationStore();
  const [designKits, setDesignKits] = useState<DesignKit[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [loading, setLoading] = useState(true);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarVisible &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setSidebarVisible(false);
      }
    };

    if (sidebarVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebarVisible]);

  useEffect(() => {
    const fetchDesignKits = async () => {
      try {
        setLoading(true);

        // Filter style conversations and transform them to design kits
        const styleConversations = conversations.filter(
          (c) => c.collectionType === "STYLE",
        );

        const kits: DesignKit[] = styleConversations.map((conv) => ({
          id: conv.id,
          title: conv.title,
          status: "active" as const,
          lastModified: new Date(conv.lastActivity),
          createdAt: new Date(conv.createdAt),
          category: "presentation",
          tags: ["design", "ai-generated", "modern"],
          description: `Design kit with ${conv.messageCount} style variations`,
          colorPalette: ["#3B82F6", "#10B981", "#F59E0B", "#EF4444"],
          fontFamily: "Inter",
        }));

        setDesignKits(kits);
      } catch (error) {
        console.error("Failed to load design kits:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDesignKits();
  }, [conversations]);

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  const filteredKits = designKits.filter((kit) => {
    const matchesSearch =
      kit.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      kit.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      kit.tags.some((tag) =>
        tag.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    const matchesCategory =
      filterCategory === "all" || kit.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 border-green-200";
      case "draft":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "archived":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "presentation":
        return "bg-accent text-accent-foreground border-primary/30";
      case "marketing":
        return "bg-accent text-accent-foreground border-primary/30";
      case "corporate":
        return "bg-accent text-accent-foreground border-primary/30";
      case "creative":
        return "bg-accent text-accent-foreground border-primary/30";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const handleKitClick = (kit: DesignKit) => {
    navigate({
      to: "/chat/$conversationId/gallery",
      params: { conversationId: kit.id },
      search: { slide: undefined, tab: undefined },
    });
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-muted-foreground">
          Loading design kits...
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-background relative overflow-hidden">
      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`absolute left-0 top-0 h-full w-80 md:w-80 sm:w-72 border-r border-border bg-background/95 backdrop-blur-md transition-transform duration-300 ease-out z-[60] shadow-xl ${
          sidebarVisible ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="h-full flex flex-col">
          <div className="border-b border-border p-4">
            <h3
              className="text-sm font-semibold cursor-pointer text-primary"
              onClick={() =>
                navigate({
                  to: "/",
                  params: {},
                  search: { slide: undefined },
                })
              }
            >
              Go to Home
            </h3>
          </div>
          <div className="flex-1 overflow-hidden">
            <Sidebar onChatSelect={() => setSidebarVisible(false)} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col h-full flex-1 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        {/* Header */}
        <div className="flex-shrink-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200/50 dark:border-slate-700/50 shadow-sm">
          <div className="px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Sidebar Toggle Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSidebar}
                  className="h-10 w-10 p-0 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </Button>

                <div className="w-12 h-12 rounded-xl flex items-center justify-center border border-primary/30 bg-primary/10">
                  <Palette className="w-6 h-6 text-primary dark:text-accent-foreground" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-primary">
                    Design Kits
                  </h1>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    Browse and customise professional design styles
                  </p>
                </div>
              </div>

              {/* Header Actions */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                  <Button
                    variant={viewMode === "grid" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("grid")}
                    className="h-8 px-3"
                  >
                    <Grid3X3 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === "list" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("list")}
                    className="h-8 px-3"
                  >
                    <List className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="flex items-center gap-4 mt-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search design kits..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                />
              </div>

              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="all">All Categories</option>
                  <option value="presentation">Presentation</option>
                  <option value="marketing">Marketing</option>
                  <option value="corporate">Corporate</option>
                  <option value="creative">Creative</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-8">
          {filteredKits.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full max-w-md mx-auto text-center">
              <div className="w-24 h-24 rounded-full flex items-center justify-center mb-6 bg-primary/10">
                <Palette className="w-12 h-12 text-primary dark:text-accent-foreground opacity-60" />
              </div>
              <h3 className="text-xl font-semibold text-slate-700 dark:text-slate-300 mb-2">
                No Design Kits Found
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                {searchQuery || filterCategory !== "all"
                  ? "Try adjusting your search or filter criteria."
                  : "Create your first design kit to get started with professional styling."}
              </p>
              {!searchQuery && filterCategory === "all" && (
                <Button
                  onClick={() =>
                    navigate({
                      to: "/",
                      search: { slide: undefined },
                    })
                  }
                  className="text-primary-foreground bg-primary hover:bg-primary/90"
                >
                  Create Design Kit
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Kits Count */}
              <div className="flex items-center justify-between mb-6">
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  {filteredKits.length} design kit
                  {filteredKits.length !== 1 ? "s" : ""} found
                </div>
              </div>

              {/* Kits Grid/List */}
              {viewMode === "grid" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredKits.map((kit) => (
                    <Card
                      key={kit.id}
                      className="group bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-primary/30 dark:hover:border-primary transition-all duration-200 hover:shadow-lg hover:shadow-primary/10 cursor-pointer"
                      onClick={() => handleKitClick(kit)}
                    >
                      {/* Preview */}
                      <div className="aspect-video rounded-t-lg flex items-center justify-center relative overflow-hidden bg-accent">
                        {kit.previewImage ? (
                          <img
                            src={kit.previewImage}
                            alt={kit.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="flex flex-col items-center gap-2">
                            <Paintbrush className="w-8 h-8 text-primary" />
                            <span className="text-xs text-primary dark:text-accent-foreground font-medium">
                              Design Kit
                            </span>
                          </div>
                        )}

                        {/* View Overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-200 flex items-center justify-center">
                          <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 transform scale-75 group-hover:scale-100">
                            <Eye className="w-5 h-5 text-primary" />
                          </div>
                        </div>

                        {/* Status Badge */}
                        <div className="absolute top-3 left-3">
                          <Badge
                            className={`text-xs font-medium ${getStatusColor(kit.status)}`}
                          >
                            {kit.status}
                          </Badge>
                        </div>

                        {/* Color Palette */}
                        {kit.colorPalette && (
                          <div className="absolute bottom-3 right-3 flex gap-1">
                            {kit.colorPalette
                              .slice(0, 4)
                              .map((color, index) => (
                                <div
                                  key={index}
                                  className="w-3 h-3 rounded-full border border-white/50"
                                  style={{ backgroundColor: color }}
                                />
                              ))}
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm line-clamp-2 flex-1 mr-2">
                            {kit.title}
                          </h3>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-all duration-200"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </div>

                        {kit.description && (
                          <p className="text-xs text-slate-600 dark:text-slate-400 mb-3 line-clamp-2">
                            {kit.description}
                          </p>
                        )}

                        {/* Category & Font */}
                        <div className="flex items-center gap-2 mb-3">
                          <Badge
                            className={`text-xs font-medium ${getCategoryColor(kit.category)}`}
                          >
                            {kit.category}
                          </Badge>
                          {kit.fontFamily && (
                            <Badge
                              variant="outline"
                              className="text-xs px-2 py-0.5"
                            >
                              {kit.fontFamily}
                            </Badge>
                          )}
                        </div>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-1 mb-3">
                          {kit.tags.slice(0, 2).map((tag) => (
                            <Badge
                              key={tag}
                              variant="secondary"
                              className="text-xs px-2 py-0.5"
                            >
                              {tag}
                            </Badge>
                          ))}
                          {kit.tags.length > 2 && (
                            <Badge
                              variant="secondary"
                              className="text-xs px-2 py-0.5"
                            >
                              +{kit.tags.length - 2}
                            </Badge>
                          )}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{formatDate(kit.lastModified)}</span>
                          </div>
                          {kit.styleCount && (
                            <div className="flex items-center gap-1">
                              <Palette className="w-3 h-3" />
                              <span>{kit.styleCount} styles</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredKits.map((kit) => (
                    <Card
                      key={kit.id}
                      className="group bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-primary/30 dark:hover:border-primary transition-all duration-200 hover:shadow-lg hover:shadow-primary/10 cursor-pointer"
                      onClick={() => handleKitClick(kit)}
                    >
                      <div className="p-4">
                        <div className="flex items-center gap-4">
                          {/* Preview */}
                          <div className="w-20 h-12 rounded-lg flex items-center justify-center flex-shrink-0 relative bg-accent">
                            <Paintbrush className="w-5 h-5 text-primary" />

                            {/* Color Palette */}
                            {kit.colorPalette && (
                              <div className="absolute -bottom-1 -right-1 flex gap-0.5">
                                {kit.colorPalette
                                  .slice(0, 3)
                                  .map((color, index) => (
                                    <div
                                      key={index}
                                      className="w-2 h-2 rounded-full border border-white/50"
                                      style={{ backgroundColor: color }}
                                    />
                                  ))}
                              </div>
                            )}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-sm truncate">
                                {kit.title}
                              </h3>
                              <div className="flex items-center gap-2">
                                <Badge
                                  className={`text-xs font-medium ${getStatusColor(kit.status)}`}
                                >
                                  {kit.status}
                                </Badge>
                                <Badge
                                  className={`text-xs font-medium ${getCategoryColor(kit.category)}`}
                                >
                                  {kit.category}
                                </Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-all duration-200"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>

                            {kit.description && (
                              <p className="text-xs text-slate-600 dark:text-slate-400 mb-2 line-clamp-1">
                                {kit.description}
                              </p>
                            )}

                            <div className="flex items-center justify-between">
                              <div className="flex flex-wrap gap-1">
                                {kit.tags.slice(0, 3).map((tag) => (
                                  <Badge
                                    key={tag}
                                    variant="secondary"
                                    className="text-xs px-2 py-0.5"
                                  >
                                    {tag}
                                  </Badge>
                                ))}
                                {kit.fontFamily && (
                                  <Badge
                                    variant="outline"
                                    className="text-xs px-2 py-0.5"
                                  >
                                    {kit.fontFamily}
                                  </Badge>
                                )}
                              </div>

                              <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                                <div className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  <span>{formatDate(kit.lastModified)}</span>
                                </div>
                                {kit.styleCount && (
                                  <div className="flex items-center gap-1">
                                    <Palette className="w-3 h-3" />
                                    <span>{kit.styleCount} styles</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
